import { Injectable, OnDestroy } from '@angular/core';
import { SuggestionHttpService } from './suggestion.http.service';
import { objectToCamel } from 'ts-case-convert';
import { BehaviorSubject, lastValueFrom, Subscription, interval } from 'rxjs';
import { SuggestionSortType } from 'src/app/models/enums/suggestionSortType';
import { ISuggestion } from 'src/app/models/suggestion';
import { ISort } from 'src/app/models/sort';
import { IActiveTicketsByExpedition } from 'src/app/models/activeTicket';
import { CommunicationUserType } from 'src/app/models/enums';
import { ISuggestionsFilters } from 'src/app/filters/suggestionsFilters';

@Injectable({
  providedIn: 'root',
})
export class SuggestionService implements OnDestroy {
  private pollingSubscription: Subscription | null = null;
  private readonly POLLING_INTERVAL = 10 * 60 * 1000; // 10 minutes

  private pendingCountSubject = new BehaviorSubject<number>(0);
  public pendingCount$ = this.pendingCountSubject.asObservable();

  constructor(private suggestionHttpService: SuggestionHttpService) {}

  ngOnDestroy() {
    this.stopPolling();
  }

  startPolling() {
    this.fetchPendingSuggestionsCount();

    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    this.pollingSubscription = interval(this.POLLING_INTERVAL).subscribe(() => {
      this.fetchPendingSuggestionsCount();
    });
  }

  stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  async fetchPendingSuggestionsCount() {
    try {
      const source$ = this.suggestionHttpService.getPendingSuggestionsCount();
      const response = await lastValueFrom(source$);
      const count = (response as number) ?? 0;
      this.pendingCountSubject.next(count);
      return count;
    } catch (error) {
      console.error('Error fetching pending suggestions count:', error);
      return 0;
    }
  }

  getPendingCount() {
    return this.pendingCountSubject.value;
  }

  async createSuggestion(suggestion: ISuggestion) {
    const source$ = this.suggestionHttpService.createSuggestion(suggestion);
    const response = await lastValueFrom(source$);
    const suggestionCreated = objectToCamel(response as object);
    return suggestionCreated;
  }
  
  async updateSuggestion(suggestion: ISuggestion) {
    const source$ = this.suggestionHttpService.updateSuggestion(suggestion);
    const response = await lastValueFrom(source$);
    const suggestionUpdated = objectToCamel(response as object);
    this.fetchPendingSuggestionsCount();
    return suggestionUpdated;
  }
  
  async getSuggestions(userType: CommunicationUserType, page?: number, size?: number) {
    const source$ = this.suggestionHttpService.getSuggestions(
      userType,
      page,
      size,
      SuggestionSortType.CREATION
    );
    const response = await lastValueFrom(source$);
    const suggestions = objectToCamel(response as object);
    const suggestionsArray = this.getData(suggestions);
    return suggestionsArray;
  }
  
  async getSuggestionById(id: number) {
    const source$ = this.suggestionHttpService.getSuggestionById(id)
    const response = await lastValueFrom(source$);
    const suggestion = objectToCamel(response as object);
    return suggestion;
  }

  async filterSuggestions(
    communicationUserType: CommunicationUserType,
    page: number,
    size: number,
    sort: ISort<SuggestionSortType>,
    filters: ISuggestionsFilters,
    searchValue?: string,
  ) {
    const source$ = this.suggestionHttpService.filter(
      communicationUserType,
      page,
      size,
      sort,
      filters,
      searchValue,
    );
    const response = await lastValueFrom(source$);
    const suggestions = objectToCamel(response as object);
    const suggestionsArray = this.getData(suggestions);
    return suggestionsArray;
  }

  async getActiveTickets(expeditionIds: (number | null | undefined)[]): Promise<IActiveTicketsByExpedition> {
    const uniqueExpeditionIds = [...new Set(expeditionIds.filter((id): id is number => !!id))];
    if (uniqueExpeditionIds.length === 0) return {};
    const source$ = this.suggestionHttpService.getActiveTickets(uniqueExpeditionIds);
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object) as IActiveTicketsByExpedition;
  }

  async sendConversationMessage(suggestionId: number, newMessage: string) {
    const source$ = this.suggestionHttpService.sendConversationMessage(suggestionId, newMessage);
    const response = await lastValueFrom(source$);
    const updatedSuggestion = objectToCamel(response as object);
    return updatedSuggestion;
  }

  getData(response: any) {
    return response.data;
  }

}
